import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { type FleetAdminRequest, fleetAdminField } from '../../../../app/types';
import { AccountType } from '../../../../services/schemas/accountsBackendCodec';
import { FormInputField } from '../FormInputField';
import { LanguageSelect, type Locale } from '../LanguageSelect';
import { emailSchema, nameSchema, phoneSchema, reasonSchema } from '../schema';

interface AddFleetAdminDialogProps {
    accountName: string;
    accountType: string | undefined;
    showDialog: boolean;
    setShowDialog: (value: boolean) => void;
    handleAddAdminConfirm: (fleetAdmin: FleetAdminRequest) => void;
}

const emptyFleetAdminRequest: FleetAdminRequest = {
    firstname: undefined,
    lastname: undefined,
    email: undefined,
    phoneNumber: undefined,
    language: undefined,
    reason: undefined,
    isGeneralContact: undefined,
    isBillingContact: undefined,
};

type FormErrors = { [key: string]: string | undefined } | undefined;

function getAdminTypeFor(accountType: string | undefined) {
    switch (accountType) {
        case AccountType.CARRIER:
            return 'Fleet Admin';
        case AccountType.BUYER:
            return 'Buyer Admin';
        case AccountType.SUPPLIER:
            return 'Supplier Admin';
        default:
            return 'Fleet Admin';
    }
}

export const AddAdminDialog = (props: AddFleetAdminDialogProps) => {
    const { accountName, accountType, showDialog, setShowDialog, handleAddAdminConfirm } = props;

    const [fleetAdminToCreate, setFleetAdminToCreate] = useState(emptyFleetAdminRequest);
    const [formError, setFormError] = useState<FormErrors>({});

    useEffect(() => {
        setFormError({
            [fleetAdminField.FIRST_NAME]:
                fleetAdminToCreate.firstname !== undefined &&
                !nameSchema.safeParse(fleetAdminToCreate.firstname).success
                    ? 'intl-msg:helpgang.addFleetAdminDialog.name.error'
                    : undefined,
            [fleetAdminField.LAST_NAME]:
                fleetAdminToCreate.lastname !== undefined && !nameSchema.safeParse(fleetAdminToCreate.lastname).success
                    ? 'intl-msg:helpgang.addFleetAdminDialog.name.error'
                    : undefined,
            [fleetAdminField.EMAIL]:
                fleetAdminToCreate.email !== undefined && !emailSchema.safeParse(fleetAdminToCreate.email).success
                    ? 'intl-msg:helpgang.addFleetAdminDialog.email.error'
                    : undefined,
            [fleetAdminField.PHONE_NUMBER]:
                fleetAdminToCreate.phoneNumber !== undefined &&
                fleetAdminToCreate.phoneNumber !== '' &&
                !phoneSchema.safeParse(fleetAdminToCreate.phoneNumber).success
                    ? 'intl-msg:helpgang.addFleetAdminDialog.phone.error'
                    : undefined,
            [fleetAdminField.REASON]:
                fleetAdminToCreate.reason !== undefined &&
                fleetAdminToCreate.reason !== '' &&
                !reasonSchema.safeParse(fleetAdminToCreate.reason).success
                    ? 'intl-msg:helpgang.addFleetAdminDialog.reason.error'
                    : undefined,
        });
    }, [fleetAdminToCreate]);

    const handlePropertyChange = (propertyName: string, value: string | boolean) => {
        setFleetAdminToCreate(prevState => ({
            ...prevState,
            [propertyName]: value,
        }));
    };

    const isNullOrEmpty = (s: string | null | undefined) => {
        return s == null || s.trim().length === 0;
    };

    const isInvalidPhoneNumber = (s: string | null | undefined) => {
        if (s === null || s === undefined || s === '') {
            return false;
        } else {
            return !phoneSchema.safeParse(fleetAdminToCreate.phoneNumber).success;
        }
    };

    const isValidForm = () =>
        fleetAdminToCreate.firstname !== '' &&
        fleetAdminToCreate.firstname !== undefined &&
        fleetAdminToCreate.lastname !== '' &&
        fleetAdminToCreate.lastname !== undefined &&
        fleetAdminToCreate.email !== '' &&
        fleetAdminToCreate.email !== undefined &&
        emailSchema.safeParse(fleetAdminToCreate.email).success &&
        fleetAdminToCreate.reason !== '' &&
        fleetAdminToCreate.reason !== undefined &&
        (fleetAdminToCreate.phoneNumber ? phoneSchema.safeParse(fleetAdminToCreate.phoneNumber).success : true);

    const onConfirm = () => {
        handleAddAdminConfirm(fleetAdminToCreate);
        setFleetAdminToCreate(emptyFleetAdminRequest);
    };

    const onCancel = () => {
        setShowDialog(false);
        setFleetAdminToCreate(emptyFleetAdminRequest);
    };

    const getModalContent = () => (
        <div data-cy={'modal-content-add-fleet-admin'} className='form-group'>
            <p>
                <FormattedMessage
                    id={'intl-msg:helpgang.addFleetAdminDialog.content'}
                    values={{ b: chunks => <b>{chunks}</b>, accountName: accountName }}
                />
            </p>
            <FormInputField
                dataAttribute='add-fleet-admin-dialog-first-name-input'
                translationId={'intl-msg:helpgang.addFleetAdminDialog.firstName'}
                value={fleetAdminToCreate.firstname}
                propertyName={fleetAdminField.FIRST_NAME}
                // biome-ignore lint/complexity/useOptionalChain: suppressed out of convenience for migration to biomejs
                formError={formError && formError[fleetAdminField.FIRST_NAME]}
                autoFocus={true}
                required={true}
                onValueChange={handlePropertyChange}
            />
            <FormInputField
                dataAttribute='add-fleet-admin-dialog-last-name-input'
                translationId={'intl-msg:helpgang.addFleetAdminDialog.lastName'}
                value={fleetAdminToCreate.lastname}
                propertyName={fleetAdminField.LAST_NAME}
                // biome-ignore lint/complexity/useOptionalChain: suppressed out of convenience for migration to biomejs
                formError={formError && formError[fleetAdminField.LAST_NAME]}
                required={true}
                onValueChange={handlePropertyChange}
            />
            <FormInputField
                dataAttribute='add-fleet-admin-dialog-email-input'
                translationId={'intl-msg:helpgang.addFleetAdminDialog.email'}
                placeholder={'max.mustermann@example.com'}
                value={fleetAdminToCreate.email}
                propertyName={fleetAdminField.EMAIL}
                hasError={isNullOrEmpty(fleetAdminToCreate.email)}
                // biome-ignore lint/complexity/useOptionalChain: suppressed out of convenience for migration to biomejs
                formError={formError && formError[fleetAdminField.EMAIL]}
                required={true}
                onValueChange={handlePropertyChange}
            />
            <div className={'form-group margin-left-20'}>
                <Checkbox
                    name='general_contact_email'
                    onClick={() =>
                        handlePropertyChange(fleetAdminField.IS_GENERAL_CONTACT, !fleetAdminToCreate.isGeneralContact)
                    }
                >
                    <div className='text-color-dark'>
                        <FormattedMessage id={'intl-msg:helpgang.addFleetAdminDialog.useEmailAsGeneralContact'} />
                    </div>
                </Checkbox>
            </div>
            <div className={'form-group margin-left-20'}>
                <Checkbox
                    name='billing_contact_email'
                    onClick={() =>
                        handlePropertyChange(fleetAdminField.IS_BILLING_CONTACT, !fleetAdminToCreate.isBillingContact)
                    }
                >
                    <div className='text-color-dark'>
                        <FormattedMessage id={'intl-msg:helpgang.addFleetAdminDialog.useEmailAsBillingContact'} />
                    </div>
                </Checkbox>
            </div>
            <FormInputField
                dataAttribute='add-fleet-admin-dialog-phone-input'
                translationId={'intl-msg:helpgang.addFleetAdminDialog.phone'}
                placeholder={'+4989123456789'}
                value={fleetAdminToCreate.phoneNumber}
                propertyName={fleetAdminField.PHONE_NUMBER}
                hasError={isInvalidPhoneNumber(fleetAdminToCreate.phoneNumber)}
                // biome-ignore lint/complexity/useOptionalChain: suppressed out of convenience for migration to biomejs
                formError={formError && formError[fleetAdminField.PHONE_NUMBER]}
                required={false}
                onValueChange={handlePropertyChange}
            />
            <LanguageSelect
                handleLocaleChange={(value: Locale) => handlePropertyChange(fleetAdminField.LANGUAGE, value)}
            />

            <FormInputField
                dataAttribute='add-fleet-admin-dialog-reason-input'
                translationId={'intl-msg:helpgang.addFleetAdminDialog.reason'}
                value={fleetAdminToCreate.reason}
                propertyName={fleetAdminField.REASON}
                // biome-ignore lint/complexity/useOptionalChain: suppressed out of convenience for migration to biomejs
                formError={formError && formError[fleetAdminField.REASON]}
                required={true}
                onValueChange={handlePropertyChange}
            />

            <div className={'callout callout-warning text-color-warning line-height-125rel margin-bottom-10'}>
                <FormattedMessage id={'intl-msg:helpgang.addFleetAdminDialog.selfInviteWarning'} />
            </div>
        </div>
    );

    const adminType = getAdminTypeFor(accountType);
    return (
        <ConfirmationDialog
            show={showDialog}
            bsSize={'sm'}
            title={<FormattedMessage id={'intl-msg:helpgang.addFleetAdminDialog.title'} values={{ adminType }} />}
            content={getModalContent()}
            confirmButtonText={<FormattedMessage id={'intl-msg:helpgang.confirmationDialog.create'} />}
            disableConfirm={!isValidForm()}
            onClickConfirm={onConfirm}
            cancelButtonText={<FormattedMessage id={'intl-msg:helpgang.confirmationDialog.cancel'} />}
            onClickCancel={onCancel}
            useOverflow={false}
        />
    );
};
